import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  GridLegacy as Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Lock, Save, Send } from '@mui/icons-material';
import { managerAPI } from '../../services/api';
import { tokens } from '../../theme';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD'];
const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'AED', SGD: 'S$', AUD: 'A$', CAD: 'C$',
};
const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];
const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full time' },
  { value: 'part_time', label: 'Part time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

// Fields the server freezes once recruiters are sourcing, because changing who
// is being looked for would invalidate CVs already submitted against the brief.
const LOCKED_ONCE_SOURCING = new Set([
  'role_title', 'department', 'expertise', 'min_experience_years', 'max_experience_years',
  'location', 'employment_type', 'budget_min', 'budget_max', 'budget_currency',
]);

const EMPTY = {
  role_title: '',
  department: '',
  expertise: [],
  min_experience_years: '',
  max_experience_years: '',
  headcount: '1',
  location: '',
  employment_type: 'full_time',
  budget_min: '',
  budget_max: '',
  budget_currency: 'INR',
  priority: 'medium',
  target_onboarding_date: '',
  description: '',
  justification: '',
};

/**
 * Create or edit a hiring requirement.
 *
 * Validation here mirrors the server's rather than trusting it to be the only
 * gate, so a manager sees the problem next to the field instead of a single
 * error at the top after submitting. When a requirement is already being
 * sourced, the fields the server will refuse are disabled and labelled, so the
 * restriction is visible before it is hit.
 */
const RequirementForm = ({ open, onClose, requirement, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [titles, setTitles] = useState([]);

  const isEdit = !!requirement?.id;
  const partiallyLocked = isEdit && requirement?.is_editable === false;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setServerError(null);
    if (requirement) {
      setForm({
        role_title: requirement.role_title || '',
        department: requirement.department || '',
        expertise: requirement.skills || [],
        min_experience_years:
          requirement.min_experience_years != null ? String(requirement.min_experience_years) : '',
        max_experience_years:
          requirement.max_experience_years != null ? String(requirement.max_experience_years) : '',
        headcount: requirement.headcount != null ? String(requirement.headcount) : '1',
        location: requirement.location || '',
        employment_type: requirement.employment_type || 'full_time',
        budget_min: requirement.budget_min != null ? String(requirement.budget_min) : '',
        budget_max: requirement.budget_max != null ? String(requirement.budget_max) : '',
        budget_currency: requirement.budget_currency || 'INR',
        priority: requirement.priority || 'medium',
        target_onboarding_date: requirement.target_onboarding_date
          ? String(requirement.target_onboarding_date).slice(0, 10)
          : '',
        description: requirement.description || '',
        justification: requirement.justification || '',
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, requirement]);

  useEffect(() => {
    if (!open) return;
    managerAPI
      .getJobTitles()
      .then((r) => setTitles((r.data?.data || []).map((t) => t.title).filter(Boolean)))
      .catch(() => setTitles([]));
  }, [open]);

  const setField = (name) => (event) => {
    const value = event?.target ? event.target.value : event;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = useCallback(() => {
    const found = {};
    if (!form.role_title.trim()) found.role_title = 'Required';
    if (!form.location.trim()) found.location = 'Required';
    if (!form.expertise.length) found.expertise = 'Add at least one skill';

    const low = form.min_experience_years === '' ? null : Number(form.min_experience_years);
    const high = form.max_experience_years === '' ? null : Number(form.max_experience_years);
    if (low != null && (Number.isNaN(low) || low < 0 || low > 60)) {
      found.min_experience_years = 'Between 0 and 60';
    }
    if (high != null && (Number.isNaN(high) || high < 0 || high > 60)) {
      found.max_experience_years = 'Between 0 and 60';
    }
    if (low != null && high != null && high < low) {
      found.max_experience_years = 'Cannot be below the minimum';
    }

    const headcount = Number(form.headcount);
    if (!form.headcount || Number.isNaN(headcount) || headcount < 1 || headcount > 999) {
      found.headcount = 'Between 1 and 999';
    }

    const bMin = form.budget_min === '' ? null : Number(form.budget_min);
    const bMax = form.budget_max === '' ? null : Number(form.budget_max);
    if (bMin != null && (Number.isNaN(bMin) || bMin < 0)) found.budget_min = 'Not a valid amount';
    if (bMax != null && (Number.isNaN(bMax) || bMax < 0)) found.budget_max = 'Not a valid amount';
    if (bMin != null && bMax != null && bMax < bMin) found.budget_max = 'Cannot be below the minimum';

    setErrors(found);
    return Object.keys(found).length === 0;
  }, [form]);

  const buildPayload = useCallback(() => {
    const payload = {
      role_title: form.role_title.trim(),
      department: form.department.trim() || null,
      expertise: form.expertise,
      min_experience_years: form.min_experience_years === '' ? 0 : Number(form.min_experience_years),
      max_experience_years: form.max_experience_years === '' ? null : Number(form.max_experience_years),
      headcount: Number(form.headcount),
      location: form.location.trim(),
      employment_type: form.employment_type,
      budget_min: form.budget_min === '' ? null : Number(form.budget_min),
      budget_max: form.budget_max === '' ? null : Number(form.budget_max),
      budget_currency: form.budget_currency,
      priority: form.priority,
      target_onboarding_date: form.target_onboarding_date || null,
      description: form.description.trim() || null,
      justification: form.justification.trim() || null,
    };

    // Sending a locked field back unchanged would still be refused with a 409,
    // so an edit of a requirement already in sourcing only carries what it may.
    if (partiallyLocked) {
      Object.keys(payload).forEach((key) => {
        if (LOCKED_ONCE_SOURCING.has(key)) delete payload[key];
      });
    }
    return payload;
  }, [form, partiallyLocked]);

  const submit = async (publish) => {
    if (!validate()) return;
    setSaving(true);
    setServerError(null);
    try {
      let response;
      if (isEdit) {
        response = await managerAPI.updateRequirement(requirement.id, buildPayload());
        if (publish && requirement.status === 'draft') {
          response = await managerAPI.publishRequirement(requirement.id);
        }
      } else {
        response = await managerAPI.createRequirement({ ...buildPayload(), publish });
      }
      onSaved?.(response.data);
      onClose?.();
    } catch (e) {
      setServerError(e.response?.data?.error || 'Could not save the requirement.');
    } finally {
      setSaving(false);
    }
  };

  const symbol = CURRENCY_SYMBOLS[form.budget_currency] || form.budget_currency;
  const canPublish = !isEdit || requirement?.status === 'draft';

  const lockProps = (name) =>
    partiallyLocked && LOCKED_ONCE_SOURCING.has(name)
      ? { disabled: true, helperText: 'Locked — recruiters are already sourcing' }
      : {};

  const band = useMemo(() => {
    const low = form.min_experience_years;
    const high = form.max_experience_years;
    if (!low && !high) return null;
    if (low && high) return `${low} – ${high} years`;
    return high ? `Up to ${high} years` : `${low}+ years`;
  }, [form.min_experience_years, form.max_experience_years]);

  return (
    <Dialog open={!!open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }} noWrap>
              {isEdit ? `Edit ${requirement.requirement_code || 'requirement'}` : 'New hiring requirement'}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }}>
              Describe the expertise and experience you need. Publishing sends it to vendor/HR.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {partiallyLocked && (
          <Alert severity="info" icon={<Lock fontSize="small" />} sx={{ mb: 2 }}>
            Recruiters have started sourcing against this requirement. The role, skills, experience
            band, location and budget are locked; priority, openings, target date and the
            description can still change.
          </Alert>
        )}
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
            The role
          </Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Autocomplete
              freeSolo
              options={titles}
              value={form.role_title}
              disabled={partiallyLocked && LOCKED_ONCE_SOURCING.has('role_title')}
              onInputChange={(_e, value) => setField('role_title')(value ?? '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Role / expertise needed"
                  placeholder="e.g. Senior Python Engineer"
                  error={!!errors.role_title}
                  helperText={errors.role_title || lockProps('role_title').helperText || ' '}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Department"
              value={form.department}
              onChange={setField('department')}
              helperText=" "
              {...lockProps('department')}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.expertise}
              disabled={partiallyLocked && LOCKED_ONCE_SOURCING.has('expertise')}
              onChange={(_e, value) => setField('expertise')(value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Skills / expertise"
                  placeholder="Type a skill and press Enter"
                  error={!!errors.expertise}
                  helperText={errors.expertise || lockProps('expertise').helperText || 'Press Enter after each skill'}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Min experience"
              value={form.min_experience_years}
              onChange={setField('min_experience_years')}
              inputProps={{ min: 0, max: 60, step: 0.5 }}
              error={!!errors.min_experience_years}
              helperText={errors.min_experience_years || lockProps('min_experience_years').helperText || 'Years'}
              {...lockProps('min_experience_years')}
              InputProps={{ endAdornment: <InputAdornment position="end">yrs</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Max experience"
              value={form.max_experience_years}
              onChange={setField('max_experience_years')}
              inputProps={{ min: 0, max: 60, step: 0.5 }}
              error={!!errors.max_experience_years}
              helperText={errors.max_experience_years || lockProps('max_experience_years').helperText || band || 'Optional'}
              {...lockProps('max_experience_years')}
              InputProps={{ endAdornment: <InputAdornment position="end">yrs</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Openings"
              value={form.headcount}
              onChange={setField('headcount')}
              inputProps={{ min: 1, max: 999 }}
              error={!!errors.headcount}
              helperText={errors.headcount || ' '}
            />
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              select
              label="Employment type"
              value={form.employment_type}
              onChange={setField('employment_type')}
              helperText=" "
              {...lockProps('employment_type')}
            >
              {EMPLOYMENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Location"
              value={form.location}
              onChange={setField('location')}
              error={!!errors.location}
              helperText={errors.location || lockProps('location').helperText || ' '}
              {...lockProps('location')}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={form.priority}
              onChange={setField('priority')}
              helperText=" "
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Target onboarding"
              value={form.target_onboarding_date}
              onChange={setField('target_onboarding_date')}
              InputLabelProps={{ shrink: true }}
              helperText="Becomes the job deadline"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mt: 1, mb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                Budget
              </Typography>
            </Divider>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              select
              label="Currency"
              value={form.budget_currency}
              onChange={setField('budget_currency')}
              helperText=" "
              {...lockProps('budget_currency')}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Budget from"
              value={form.budget_min}
              onChange={setField('budget_min')}
              error={!!errors.budget_min}
              helperText={errors.budget_min || lockProps('budget_min').helperText || 'Annual, optional'}
              {...lockProps('budget_min')}
              InputProps={{ startAdornment: <InputAdornment position="start">{symbol}</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} md={5}>
            <TextField
              fullWidth
              type="number"
              label="Budget to"
              value={form.budget_max}
              onChange={setField('budget_max')}
              error={!!errors.budget_max}
              helperText={errors.budget_max || lockProps('budget_max').helperText || 'Annual, optional'}
              {...lockProps('budget_max')}
              InputProps={{ startAdornment: <InputAdornment position="start">{symbol}</InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mt: 1, mb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.color.inkTertiary }}>
                Context
              </Typography>
            </Divider>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="What the person will do"
              value={form.description}
              onChange={setField('description')}
              helperText="Shown to recruiters and used as the job description"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Why this headcount is needed"
              value={form.justification}
              onChange={setField('justification')}
              helperText="Backfill, growth, attrition — helps approvals and prioritisation"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="outlined"
          onClick={() => submit(false)}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <Save fontSize="small" />}
        >
          {isEdit ? 'Save changes' : 'Save draft'}
        </Button>
        {canPublish && (
          <Button
            variant="contained"
            onClick={() => submit(true)}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Send fontSize="small" />}
          >
            Publish to vendor/HR
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RequirementForm;
